import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

class BundleBuilder extends Component {
  /** @type {HTMLSelectElement[]} */
  #selects = [];

  /** @type {HTMLInputElement | null} */
  #variantInput = null;

  /** @type {HTMLButtonElement | null} */
  #addButton = null;

  /** @type {HTMLElement | null} */
  #price = null;

  /** @type {HTMLElement | null} */
  #comparePrice = null;

  /** @type {HTMLElement | null} */
  #error = null;

  /** @type {Array<any>} */
  #variants = [];

  /** @type {Array<{name: string, image: string | null, swatch: string}>} */
  #shades = [];

  /** @type {number | undefined} */
  #errorTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.#selects = Array.from(this.querySelectorAll('[data-bundle-select]'));
    this.#variantInput = this.querySelector('[data-bundle-variant-input]');
    this.#addButton = this.querySelector('button[name="add"]');
    this.#price = this.querySelector('[data-bundle-price]');
    this.#comparePrice = this.querySelector('[data-bundle-compare-price]');
    this.#error = this.querySelector('[data-bundle-error]');
    this.#variants = this.#readJson('[data-bundle-variants]', []);
    this.#shades = this.#readJson('[data-bundle-shades]', []);

    for (const image of this.querySelectorAll('.bundle-builder__image')) {
      if (image instanceof HTMLImageElement) {
        image.dataset.originalSrc = image.currentSrc || image.src;
      }
    }

    for (const select of this.#selects) {
      select.addEventListener('change', this.#handleSelectionChange);
    }

    const form = this.querySelector('form');
    form?.addEventListener('submit', this.#handleSubmit);

    this.#syncFromSelections();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    for (const select of this.#selects) {
      select.removeEventListener('change', this.#handleSelectionChange);
    }

    const form = this.querySelector('form');
    form?.removeEventListener('submit', this.#handleSubmit);

    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
  }

  #handleSelectionChange = () => {
    this.#syncSlotImages();
    this.#syncFromSelections();
  };

  /** @param {Event} event */
  #handleSubmit = (event) => {
    if (!this.#addButton?.disabled) return;

    event.preventDefault();
    this.#showError(this.#missingSelectionMessage());
  };

  #syncFromSelections() {
    if (this.dataset.fixedCollection === 'true') {
      const variant = this.#selectedOrFirstAvailableVariant();
      this.#applyVariant(variant, Boolean(variant?.available));
      return;
    }

    const complete = this.#selects.every((select) => select.value);

    if (!complete) {
      this.#applyVariant(null, false);
      return;
    }

    const selectedValues = this.#selects.map((select) => select.value);
    const variant = this.#variants.find((candidate) => {
      return selectedValues.every((selectedValue, index) => {
        return candidate.options?.[index] === selectedValue;
      });
    });

    this.#applyVariant(variant, Boolean(variant?.available));

    if (!variant) {
      this.#showError('This shade combination is not available.');
    } else if (!variant.available) {
      this.#showError('This shade combination is sold out.');
    } else {
      this.#clearError();
    }
  }

  #syncSlotImages() {
    for (const select of this.#selects) {
      const slot = select.closest('[data-bundle-slot]');
      const image = slot?.querySelector('.bundle-builder__image');
      if (!(image instanceof HTMLImageElement)) continue;

      const selectedText = select.selectedOptions?.[0]?.textContent || '';
      const shade = this.#findShade(select.value, selectedText);
      if (shade?.image) {
        image.src = shade.image;
        image.srcset = '';
        image.alt = select.value;
        image.classList.remove('bundle-builder__image--placeholder');
      } else {
        const originalSrc = image.dataset.originalSrc || image.src;
        image.src = originalSrc;
        image.alt = select.dataset.optionName || '';
        image.classList.add('bundle-builder__image--placeholder');
      }

      const swatchColor = shade?.swatch;
      if (swatchColor && select.value) {
        select.style.setProperty('--bundle-selected-color', swatchColor);
        select.style.backgroundColor = swatchColor;
        select.style.borderColor = swatchColor;
        select.style.color = '#fff';
      } else {
        select.style.removeProperty('--bundle-selected-color');
        select.style.removeProperty('background-color');
        select.style.removeProperty('border-color');
        select.style.removeProperty('color');
      }
    }
  }

  /** @param {any} variant */
  #applyVariant(variant, canAdd) {
    if (variant?.id && this.#variantInput) {
      this.#variantInput.value = String(variant.id);
    }

    if (this.#addButton) {
      this.#addButton.disabled = !canAdd;
    }

    if (variant) {
      this.#updatePrice(variant);
      this.#updateUrl(variant.id);
    }
  }

  /** @param {any} variant */
  #updatePrice(variant) {
    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';

    if (this.#price) {
      this.#price.textContent = formatMoney(variant.price, moneyFormat, currency);
    }

    if (!this.#comparePrice) return;

    const compareMode = this.dataset.comparePriceMode || 'automatic';
    const customComparePrice = this.dataset.customComparePrice || '';

    if (compareMode === 'custom' && customComparePrice.trim()) {
      this.#comparePrice.textContent = customComparePrice;
      this.#comparePrice.hidden = false;
    } else if (compareMode === 'automatic' && variant.compare_at_price && variant.compare_at_price > variant.price) {
      this.#comparePrice.textContent = formatMoney(variant.compare_at_price, moneyFormat, currency);
      this.#comparePrice.hidden = false;
    } else {
      this.#comparePrice.hidden = true;
      this.#comparePrice.textContent = '';
    }
  }

  /** @param {string | number} variantId */
  #updateUrl(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variantId));

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  #selectedOrFirstAvailableVariant() {
    const currentId = this.#variantInput?.value;
    return (
      this.#variants.find((variant) => String(variant.id) === String(currentId)) ||
      this.#variants.find((variant) => variant.available) ||
      this.#variants[0]
    );
  }

  /**
   * @param {string} value
   * @param {string} label
   */
  #findShade(value, label = '') {
    const candidates = [this.#normalize(value), this.#normalize(label)].filter(Boolean);

    return this.#shades.find((shade) => {
      const shadeName = this.#normalize(shade.name);
      return candidates.some((candidate) => {
        return candidate === shadeName || candidate.includes(shadeName) || shadeName.includes(candidate);
      });
    });
  }

  /** @param {string} selector */
  #readJson(selector, fallback) {
    const script = this.querySelector(selector);
    if (!script?.textContent) return fallback;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Failed to parse bundle builder JSON', error);
      return fallback;
    }
  }

  /** @param {string | undefined | null} value */
  #normalize(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/&amp;/g, '&')
      .replace(/[^a-z0-9]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  #missingSelectionMessage() {
    const missing = this.#selects.find((select) => !select.value);
    return missing?.dataset.optionName ? `Select ${missing.dataset.optionName.toLowerCase()} below.` : 'Select a color below.';
  }

  /** @param {string} message */
  #showError(message) {
    if (!this.#error) return;

    this.#error.classList.remove('hidden');

    const existingText = Array.from(this.#error.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
    if (existingText) {
      existingText.textContent = message;
    } else {
      this.#error.appendChild(document.createTextNode(message));
    }

    if (this.#errorTimeout) clearTimeout(this.#errorTimeout);
    this.#errorTimeout = window.setTimeout(() => this.#clearError(), ERROR_MESSAGE_DISPLAY_DURATION);
  }

  #clearError() {
    this.#error?.classList.add('hidden');
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilder);
}
